import { API_BASE_URL } from "../config/api";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import ProductPicker from "../components/ProductPicker";
import { appendAuthToken } from "../utils/auth";
import ReactSelect from "react-select";

const today = () => new Date().toISOString().slice(0, 10);

const COMPONENT_NAME_ALIASES = {
  "c.t": "CT",
  "c.t.": "CT",
  ct: "CT",
  "current transformer": "CT"
};

const FILLER_PATTERNS = [
  /\bneeds?\s+to\s+be\s+replac(?:e|ed)\b/gi,
  /\bneed\s+replacement\b/gi,
  /\bto\s+be\s+replac(?:e|ed)\b/gi,
  /\bare\s+failure\b/gi,
  /\bis\s+failure\b/gi,
  /\bfailure\b/gi,
  /\bfailed\b/gi,
  /\breplac(?:e|ed|ement)\b/gi,
  /\bissue\b/gi,
  /\bproblem\b/gi
];

const TITLE_CASE_EXCEPTIONS = new Set(["CT"]);

const HSN_CODE_SUGGESTIONS = [
  { code: "9987", label: "9987 – Services" },
  { code: "8536", label: "8536 – Electrical Apparatus" }
];

const emptyItem = () => ({
  itemCode: "",
  hsnCode: "",
  itemName: "",
  description: "",
  quantity: "",
  unit: "NA",
  listPrice: "",
  discountType: "percent",
  discountValue: "",
  autoUnitPrice: "",
  isUnitPriceManual: false,
  unitPrice: "",
  totalAmount: ""
});

const emptyForm = () => ({
  quotationNo: "",
  quotationDate: today(),
  validUntil: "",
  referenceNo: "",
  customerId: "",
  customerName: "",
  contactPerson: "",
  phone: "",
  email: "",
  gstNumber: "",
  lutRefNo: "",
  billingAddress: "",
  subject: "",
  gstType: "cgst_sgst",
  sgst: "9",
  cgst: "9",
  igst: "18",
  roundOffEnabled: "no",
  deliveryTime: "Within 2-3 weeks after confirmed purchase order",
  paymentTerms: "50% advance along with PO, balance before dispatch",
  warranty: "12 months against manufacturing defects",
  terms: "Prices are valid until the validity date. Taxes are extra as applicable. Freight and installation are extra unless mentioned.",
  notes: "",
  showDiscountInPdf: false,
  showOriginalReferenceInPdf: true,
  sourceQuotationId: "",
  sourceQuotationNo: "",
  applyMarkup: false,
  markupPercent: "10",
  commonDiscountType: "percent",
  overallDiscountPercent: "",
  status: "draft",
  items: [emptyItem()]
});

const formatAddress = (customer) => {
  const address = customer?.billingAddress || customer?.address;
  if (!address) return "";
  if (typeof address === "string") return address;

  return [address.street, address.area, address.city, address.state, address.pincode]
    .filter(Boolean)
    .join(", ");
};

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-GB");
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2
  });
};

const formatTitleCase = (value = "") =>
  String(value || "")
    .split(" ")
    .filter(Boolean)
    .map((word) => {
      const upperWord = word.toUpperCase();
      if (TITLE_CASE_EXCEPTIONS.has(upperWord)) return upperWord;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");

const normalizeComponentName = (value = "") => {
  const compact = String(value || "")
    .replace(/[()[\]{}]/g, " ")
    .replace(/[.:;]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();

  if (!compact) return "";

  const aliasKey = compact.toLowerCase();
  if (COMPONENT_NAME_ALIASES[aliasKey]) {
    return COMPONENT_NAME_ALIASES[aliasKey];
  }

  return formatTitleCase(compact);
};

const splitComponentText = (value = "") =>
  String(value || "")
    .split(/,|;|\n/gi)
    .map((entry) => entry.trim())
    .filter(Boolean);

const parseComponentEntry = (value = "") => {
  let source = String(value || "").trim();
  if (!source) return null;

  const quantityMatch = source.match(/\b(?:all\s+)?(\d+)\b/i);
  const quantity = quantityMatch ? Number(quantityMatch[1]) : 1;
  if (quantityMatch) {
    source = source.replace(quantityMatch[0], " ");
  }

  FILLER_PATTERNS.forEach((pattern) => {
    source = source.replace(pattern, " ");
  });

  source = source
    .replace(/\b(all|are|is|the|a|an|of|for|with|has|have|had)\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

  const normalizedName = normalizeComponentName(source);
  if (!normalizedName) return null;

  return {
    item: normalizedName,
    qty: quantity > 0 ? quantity : 1
  };
};

const parseReplacementComponents = (components = []) => {
  const quantities = new Map();

  (Array.isArray(components) ? components : [])
    .flatMap((entry) => splitComponentText(entry))
    .forEach((entry) => {
      const parsed = parseComponentEntry(entry);
      if (!parsed) return;

      quantities.set(parsed.item, (quantities.get(parsed.item) || 0) + parsed.qty);
    });

  return Array.from(quantities.entries()).map(([description, quantity]) => ({
    ...emptyItem(),
    description,
    quantity: String(quantity),
    unit: "NA"
  }));
};

const buildReportAddress = (prefill = {}) =>
  [prefill.siteAddress, prefill.city].filter(Boolean).join(", ");

const mapQuotationItems = (items = [], markupPercent = 10, applyMarkup = false) =>
  (Array.isArray(items) ? items : []).map((item) => recalculateDiscountedItem({
    ...emptyItem(),
    itemCode: item.itemCode || "",
    hsnCode: item.hsnCode || "",
    description: item.description || "",
    quantity: String(item.quantity ?? ""),
    unit: formatUnit(item.unit),
    listPrice: String(item.listPrice ?? item.unitPrice ?? ""),
    discountType: item.discountType || "percent",
    discountValue: String(item.discountValue ?? ""),
    isUnitPriceManual: Boolean(item.isUnitPriceManual),
    unitPrice: String(item.unitPrice ?? ""),
    totalAmount: String(item.totalAmount ?? "")
  }, markupPercent, applyMarkup));

const buildPrefilledQuotationForm = (prefill = {}, customers = []) => {
  const matchedCustomer = customers.find((customer) =>
    [customer._id, customer.customerId, customer.name]
      .filter(Boolean)
      .some((entry) => String(entry).trim().toLowerCase() === String(prefill.customerName || prefill.company || "").trim().toLowerCase())
  );

  const parsedItems = parseReplacementComponents(prefill.componentsToReplace);
  const reportDate = prefill.reportDate ? new Date(prefill.reportDate) : null;
  const safeQuotationDate = reportDate && !Number.isNaN(reportDate.getTime())
    ? reportDate.toISOString().slice(0, 10)
    : today();
  const customerName = prefill.customerName || prefill.company || matchedCustomer?.name || "";

  return {
    ...emptyForm(),
    quotationDate: safeQuotationDate,
    customerId: matchedCustomer?._id || "",
    customerName,
    contactPerson: prefill.contactPerson || matchedCustomer?.contactPerson || "",
    phone: prefill.phone || matchedCustomer?.phone || "",
    email: prefill.email || matchedCustomer?.email || "",
    gstNumber: matchedCustomer?.gstNumber || "",
    billingAddress: matchedCustomer ? formatAddress(matchedCustomer) : buildReportAddress(prefill),
    referenceNo: prefill.reportId || "",
    subject: prefill.reportId
      ? `Quotation for replacement components - Report ${prefill.reportId}`
      : "Quotation for replacement components",
    notes: [
      prefill.reportId ? `Generated from report ${prefill.reportId}` : "",
      prefill.reportDate ? `Report date: ${formatDate(prefill.reportDate)}` : "",
      prefill.complaint ? `Complaint: ${prefill.complaint}` : ""
    ]
      .filter(Boolean)
      .join(" | "),
    items: parsedItems.length ? parsedItems : [emptyItem()]
  };
};

const buildReQuotationForm = (quotation = {}, customers = []) => {
  const matchedCustomer = customers.find((customer) => String(customer._id || "") === String(quotation.customerId || ""));
  const customerName = quotation.customerName || matchedCustomer?.name || "";

  let newQuotationNo = "";
  if (quotation.quotationNo) {
    const match = quotation.quotationNo.match(/(.+)-R(\d+)$/);
    if (match) {
      newQuotationNo = `${match[1]}-R${parseInt(match[2], 10) + 1}`;
    } else {
      newQuotationNo = `${quotation.quotationNo}-R1`;
    }
  }

  return {
    ...emptyForm(),
    quotationNo: newQuotationNo,
    customerId: quotation.customerId || matchedCustomer?._id || "",
    customerName,
    contactPerson: quotation.contactPerson || matchedCustomer?.contactPerson || "",
    phone: quotation.phone || matchedCustomer?.phone || "",
    email: quotation.email || matchedCustomer?.email || "",
    gstNumber: quotation.gstNumber || matchedCustomer?.gstNumber || "",
    lutRefNo: quotation.lutRefNo || matchedCustomer?.lutRefNo || "",
    billingAddress: quotation.billingAddress || (matchedCustomer ? formatAddress(matchedCustomer) : ""),
    quotationDate: today(),
    validUntil: "",
    referenceNo: quotation.quotationNo ? `Re-Quotation of ${quotation.quotationNo}` : "",
    sourceQuotationId: quotation._id || "",
    sourceQuotationNo: quotation.quotationNo || "",
    subject: quotation.subject || "",
    gstType: quotation.gstType || "cgst_sgst",
    sgst: String(quotation.sgst ?? 9),
    cgst: String(quotation.cgst ?? 9),
    igst: String(quotation.igst ?? getDefaultIntegratedGst(quotation.gstType)),
    roundOffEnabled: quotation.roundOffEnabled ? "yes" : "no",
    applyMarkup: Boolean(quotation.applyMarkup ?? ((quotation.markupPercent ?? 0) > 0)),
    markupPercent: String(quotation.markupPercent ?? 10),
    deliveryTime: quotation.deliveryTime || "",
    paymentTerms: quotation.paymentTerms || "",
    warranty: quotation.warranty || "",
    terms: quotation.terms || "",
    notes: quotation.notes || "",
    showDiscountInPdf: Boolean(quotation.showDiscountInPdf),
    showOriginalReferenceInPdf: true,
    overallDiscountPercent: String(quotation.overallDiscountPercent ?? ""),
    commonDiscountType: quotation.items?.every((item) => (item.discountType || "percent") === (quotation.items?.[0]?.discountType || "percent"))
      ? (quotation.items?.[0]?.discountType || "percent")
      : "percent",
    items: mapQuotationItems(
      quotation.items,
      quotation.markupPercent ?? 10,
      quotation.applyMarkup ?? ((quotation.markupPercent ?? 0) > 0)
    ).length
      ? mapQuotationItems(
        quotation.items,
        quotation.markupPercent ?? 10,
        quotation.applyMarkup ?? ((quotation.markupPercent ?? 0) > 0)
      )
      : [emptyItem()]
  };
};

const buildDuplicateQuotationForm = (quotation = {}, customers = []) => {
  const matchedCustomer = customers.find((customer) => String(customer._id || "") === String(quotation.customerId || ""));
  const customerName = quotation.customerName || matchedCustomer?.name || "";

  return {
    ...emptyForm(),
    quotationNo: "",
    customerId: quotation.customerId || matchedCustomer?._id || "",
    customerName,
    contactPerson: quotation.contactPerson || matchedCustomer?.contactPerson || "",
    phone: quotation.phone || matchedCustomer?.phone || "",
    email: quotation.email || matchedCustomer?.email || "",
    gstNumber: quotation.gstNumber || matchedCustomer?.gstNumber || "",
    lutRefNo: quotation.lutRefNo || matchedCustomer?.lutRefNo || "",
    billingAddress: quotation.billingAddress || (matchedCustomer ? formatAddress(matchedCustomer) : ""),
    quotationDate: today(),
    validUntil: "",
    referenceNo: quotation.quotationNo ? `Duplicate of ${quotation.quotationNo}` : "",
    sourceQuotationId: "",
    sourceQuotationNo: "",
    subject: quotation.subject || "",
    gstType: quotation.gstType || "cgst_sgst",
    sgst: String(quotation.sgst ?? 9),
    cgst: String(quotation.cgst ?? 9),
    igst: String(quotation.igst ?? getDefaultIntegratedGst(quotation.gstType)),
    roundOffEnabled: quotation.roundOffEnabled ? "yes" : "no",
    applyMarkup: Boolean(quotation.applyMarkup ?? ((quotation.markupPercent ?? 0) > 0)),
    markupPercent: String(quotation.markupPercent ?? 10),
    deliveryTime: quotation.deliveryTime || "",
    paymentTerms: quotation.paymentTerms || "",
    warranty: quotation.warranty || "",
    terms: quotation.terms || "",
    notes: quotation.notes || "",
    showDiscountInPdf: Boolean(quotation.showDiscountInPdf),
    showOriginalReferenceInPdf: true,
    overallDiscountPercent: String(quotation.overallDiscountPercent ?? ""),
    commonDiscountType: quotation.items?.every((item) => (item.discountType || "percent") === (quotation.items?.[0]?.discountType || "percent"))
      ? (quotation.items?.[0]?.discountType || "percent")
      : "percent",
    items: mapQuotationItems(
      quotation.items,
      quotation.markupPercent ?? 10,
      quotation.applyMarkup ?? ((quotation.markupPercent ?? 0) > 0)
    ).length
      ? mapQuotationItems(
        quotation.items,
        quotation.markupPercent ?? 10,
        quotation.applyMarkup ?? ((quotation.markupPercent ?? 0) > 0)
      )
      : [emptyItem()]
  };
};

const buildSalesInvoicePrefill = (quotation = {}) => ({
  source: "quotation",
  entryType: "sale",
  customerId: quotation.customerId || "",
  counterpartyName: quotation.customerName || "",
  counterpartyPhone: quotation.phone || "",
  counterpartyEmail: quotation.email || "",
  counterpartyGst: quotation.gstNumber || "",
  counterpartyAddress: quotation.billingAddress || "",
  deliveryAddressType: "billing_address",
  otherDeliveryAddress: "",
  transactionDate: today(),
  reference: quotation.quotationNo || "",
  sourceQuotationId: quotation._id || "",
  sourceQuotationNo: quotation.quotationNo || "",
  notes: quotation.notes || "",
  paymentTerms: quotation.paymentTerms || "",
  warranty: quotation.warranty || "",
  terms: quotation.terms || "",
  gstType: quotation.gstType || "cgst_sgst",
  sgst: String(quotation.sgst ?? 9),
  cgst: String(quotation.cgst ?? 9),
  igst: String(quotation.igst ?? getDefaultIntegratedGst(quotation.gstType)),
  roundOffEnabled: quotation.roundOffEnabled ? "yes" : "no",
  entries: (quotation.items || []).map((item) => ({
    itemName: item.description || item.itemCode || "",
    itemCode: item.itemCode || "",
    hsnCode: item.hsnCode || "",
    description: item.description || "",
    quantity: String(item.quantity ?? ""),
    unit: formatUnit(item.unit),
    listPrice: String(item.listPrice ?? item.unitPrice ?? ""),
    discountType: item.discountType || "percent",
    discountValue: String(item.discountValue ?? ""),
    isUnitPriceManual: Boolean(item.isUnitPriceManual),
    unitPrice: String(item.unitPrice ?? ""),
    totalAmount: String(item.totalAmount ?? "")
  }))
});

const unitOptions = [
  ["NA", "NA"],
  ["Pkt", "Pkt"],
  ["No", "No"],
  ["Set", "Set"],
  ["Meter", "Meter"],
  ["Kg", "Kg"],
  ["Litre", "Litre"],
  ["Length", "Length"],
  ["Job", "Job"],
  ["Days", "Days"],
  ["Month", "Month"]
];

const formatUnit = (value = "") => {
  const unit = String(value || "").trim();
  if (!unit || unit === "-") return "NA";
  if (unit.toLowerCase() === "pcs" || unit.toLowerCase() === "unit") return "No";
  if (unit.toLowerCase() === "na") return "NA";
  return unit.charAt(0).toUpperCase() + unit.slice(1).toLowerCase();
};

const toDecimalString = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toFixed(2) : "";
};

const normalizeGstType = (value = "") => String(value || "").trim().toLowerCase();
const isIntegratedGstType = (value = "") => ["igst", "sez_supply"].includes(normalizeGstType(value));
const getDefaultIntegratedGst = (gstType = "") => normalizeGstType(gstType) === "sez_supply" ? "0" : "18";

const resolveEffectiveMarkup = (markupPercent = 0, applyMarkup = false) =>
  applyMarkup ? (Number(markupPercent) || 0) : 0;

const recalculateDiscountedItem = (item, markupPercent = 10, applyMarkup = false) => {
  const quantity = Number(item.quantity) || 0;
  const listPrice = Number(item.listPrice) || 0;
  const markup = resolveEffectiveMarkup(markupPercent, applyMarkup);
  const discountValue = Number(item.discountValue) || 0;
  const markedUpPrice = listPrice + (listPrice * markup / 100);
  const discountAmount = item.discountType === "amount"
    ? discountValue
    : (markedUpPrice * Math.min(discountValue, 100)) / 100;
  const autoUnitPrice = Math.max(markedUpPrice - discountAmount, 0);
  const isManual = Boolean(item.isUnitPriceManual);
  const manualUnitPrice = Math.max(Number(item.unitPrice) || 0, 0);
  const resolvedUnitPrice = isManual ? manualUnitPrice : autoUnitPrice;
  const totalAmount = quantity > 0 && resolvedUnitPrice > 0 ? quantity * resolvedUnitPrice : 0;
  const hasPricing = listPrice || discountValue || manualUnitPrice;

  return {
    ...item,
    autoUnitPrice: hasPricing ? autoUnitPrice.toFixed(2) : "",
    isUnitPriceManual: isManual,
    unitPrice: hasPricing ? resolvedUnitPrice.toFixed(2) : "",
    totalAmount: totalAmount ? totalAmount.toFixed(2) : ""
  };
};

export default function Quotations() {
  const location = useLocation();
  const navigate = useNavigate();
  const role = localStorage.getItem("role") || "";
  const accountsName = localStorage.getItem("accountsName") || "";
  const lastAppliedPrefillKeyRef = useRef("");
  const [customers, setCustomers] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewingQuotation, setViewingQuotation] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [quotationTab, setQuotationTab] = useState("quotations");
  const [quotationsLimit, setQuotationsLimit] = useState(20);
  const [productsLimit, setProductsLimit] = useState(20);
  const [activeDropdownId, setActiveDropdownId] = useState(null);

  const customerOptions = useMemo(() => {
    return customers.map((c) => ({
      value: c._id,
      label: c.name || c.customerId || "",
      data: c
    }));
  }, [customers]);

  const selectedCustomerOption = useMemo(() => {
    if (!form.customerId) {
      const matched = customers.find(
        (c) => (c.name || c.customerId || "") === form.customerName
      );
      if (matched) return { value: matched._id, label: matched.name || matched.customerId || "", data: matched };
      return form.customerName ? { value: "", label: form.customerName } : null;
    }
    const matched = customers.find((c) => c._id === form.customerId);
    if (matched) {
      return { value: matched._id, label: matched.name || matched.customerId || "", data: matched };
    }
    return form.customerName ? { value: form.customerId, label: form.customerName } : null;
  }, [customers, form.customerId, form.customerName]);

  const reactSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "36px",
      borderRadius: "0.25rem",
      borderColor: state.isFocused ? "#2563eb" : "#cbd5e1",
      boxShadow: state.isFocused ? "0 0 0 1px #2563eb" : "none",
      fontSize: "0.875rem",
      backgroundColor: "#ffffff",
      "&:hover": {
        borderColor: "#2563eb"
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: "#94a3b8"
    }),
    menu: (base) => ({
      ...base,
      zIndex: 60,
      borderRadius: "0.375rem",
      overflow: "hidden",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 60
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#dbeafe"
        : state.isFocused
          ? "#eff6ff"
          : "#ffffff",
      color: "#0f172a",
      fontSize: "0.875rem"
    }),
    indicatorSeparator: () => ({
      display: "none"
    })
  };

  const allQuotedProducts = useMemo(() => {
    const list = [];
    quotations.forEach((quotation) => {
      const qDate = quotation.quotationDate;
      const qNo = quotation.quotationNo || "Draft";
      const qId = quotation._id;
      const cId = quotation.customerId;
      const cName = quotation.customerName || "-";
      if (Array.isArray(quotation.items)) {
        quotation.items.forEach((item) => {
          list.push({
            quotationId: qId,
            quotationDate: qDate,
            quotationNo: qNo,
            customerId: cId,
            customerName: cName,
            productName: item.description || "",
            itemCode: item.itemCode || "",
            hsnCode: item.hsnCode || "",
            quantity: item.quantity || 0,
            unit: item.unit || "No",
            unitPrice: item.unitPrice || 0,
            totalPrice: item.totalAmount || 0,
            originalItem: item,
            originalQuotation: quotation
          });
        });
      }
    });

    // Default sorting: Newest quotation date first
    return list.sort((a, b) => new Date(b.quotationDate || 0) - new Date(a.quotationDate || 0));
  }, [quotations]);

  const getFamilyKeyAndVersion = useCallback((q, allQuotes) => {
    let current = q;
    let limit = 20;
    while (current.sourceQuotationNo && limit > 0) {
      let parent = null;
      const targetNo = current.sourceQuotationNo;
      const targetId = current.sourceQuotationId;
      for (let i = 0; i < allQuotes.length; i++) {
        const item = allQuotes[i];
        if (item.quotationNo === targetNo || item._id === targetId) {
          parent = item;
          break;
        }
      }
      if (!parent || parent._id === current._id) break;
      current = parent;
      limit--;
    }
    return current.quotationNo || current._id;
  }, []);

  const groupedFamilies = useMemo(() => {
    const groups = {};
    quotations.forEach((q) => {
      const key = getFamilyKeyAndVersion(q, quotations);
      if (!groups[key]) groups[key] = [];
      groups[key].push(q);
    });

    Object.keys(groups).forEach((key) => {
      groups[key].sort((a, b) => {
        const aVer = a.quotationNo.match(/-R(\d+)$/) ? parseInt(a.quotationNo.match(/-R(\d+)$/)[1], 10) : 0;
        const bVer = b.quotationNo.match(/-R(\d+)$/) ? parseInt(b.quotationNo.match(/-R(\d+)$/)[1], 10) : 0;
        return bVer - aVer;
      });
    });
    return groups;
  }, [quotations, getFamilyKeyAndVersion]);

  const filteredQuotations = useMemo(() => {
    const latestQuotes = Object.keys(groupedFamilies).map((key) => groupedFamilies[key][0]);

    // Default sorting: Newest quotation date first
    const sortedLatest = latestQuotes.sort((a, b) => new Date(b.quotationDate || 0) - new Date(a.quotationDate || 0));

    if (!search.trim()) return sortedLatest;
    const s = search.toLowerCase();
    return sortedLatest.filter((q) => 
      String(q.quotationNo || "").toLowerCase().includes(s) ||
      String(q.customerName || "").toLowerCase().includes(s) ||
      String(q.subject || "").toLowerCase().includes(s) ||
      formatDate(q.quotationDate).toLowerCase().includes(s) ||
      formatDate(q.validUntil).toLowerCase().includes(s) ||
      (q.items || []).some((item) => 
        String(item.description || "").toLowerCase().includes(s) ||
        String(item.itemCode || "").toLowerCase().includes(s) ||
        String(item.hsnCode || "").toLowerCase().includes(s)
      )
    );
  }, [groupedFamilies, search]);

  const filteredProducts = useMemo(() => {
    if (!search.trim()) return allQuotedProducts;
    const s = search.toLowerCase();
    return allQuotedProducts.filter((p) => 
      String(p.quotationNo || "").toLowerCase().includes(s) ||
      String(p.customerName || "").toLowerCase().includes(s) ||
      String(p.productName || "").toLowerCase().includes(s) ||
      String(p.itemCode || "").toLowerCase().includes(s) ||
      String(p.hsnCode || "").toLowerCase().includes(s) ||
      formatDate(p.quotationDate).toLowerCase().includes(s)
    );
  }, [allQuotedProducts, search]);

  const displayedQuotations = useMemo(() => {
    return filteredQuotations.slice(0, quotationsLimit);
  }, [filteredQuotations, quotationsLimit]);

  const displayedProducts = useMemo(() => {
    return filteredProducts.slice(0, productsLimit);
  }, [filteredProducts, productsLimit]);

  const loadData = async (query = "") => {
    try {
      setLoading(true);
      const [customersRes, quotationsRes] = await Promise.all([
        axios.get(API_BASE_URL + "/api/customers"),
        axios.get(API_BASE_URL + "/api/quotations", {
          params: query.trim() ? { search: query.trim() } : undefined
        })
      ]);

      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
      setQuotations(Array.isArray(quotationsRes.data) ? quotationsRes.data : []);
      setQuotationsLimit(20);
      setProductsLimit(20);
    } catch (error) {
      setCustomers([]);
      setQuotations([]);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (quotationId, newStatus) => {
    try {
      await axios.put(API_BASE_URL + `/api/quotations/${quotationId}/status`, { status: newStatus });
      setQuotations((prev) =>
        prev.map((q) => (q._id === quotationId ? { ...q, status: newStatus } : q))
      );
    } catch (error) {
      alert(error.response?.data?.message || "Failed to update quotation status");
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const handleOutsideClick = () => {
      setActiveDropdownId(null);
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  useEffect(() => {
    const quotationPrefill = location.state?.quotationPrefill;
    const isAdminPage = role === "admin" && location.pathname.startsWith("/admin");
    const prefillKey = location.state?.prefillKey || "";

    if (loading || !isAdminPage || !quotationPrefill || lastAppliedPrefillKeyRef.current === prefillKey) {
      return;
    }

    lastAppliedPrefillKeyRef.current = prefillKey;
    setEditingId("");
    setForm(buildPrefilledQuotationForm(quotationPrefill, customers));
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(location.pathname, { replace: true, state: {} });
  }, [customers, loading, location.pathname, location.state, role, navigate]);

  useEffect(() => {
    const reQuotationPrefill = location.state?.reQuotationPrefill;
    const prefillKey = location.state?.prefillKey || "";

    if (loading || !reQuotationPrefill || lastAppliedPrefillKeyRef.current === prefillKey) {
      return;
    }

    lastAppliedPrefillKeyRef.current = prefillKey;
    setEditingId("");
    setForm(buildReQuotationForm(reQuotationPrefill, customers));
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(location.pathname, { replace: true, state: {} });
  }, [customers, loading, location.pathname, location.state, navigate]);

  useEffect(() => {
    const duplicateQuotationPrefill = location.state?.duplicateQuotationPrefill;
    const prefillKey = location.state?.prefillKey || "";

    if (loading || !duplicateQuotationPrefill || lastAppliedPrefillKeyRef.current === prefillKey) {
      return;
    }

    lastAppliedPrefillKeyRef.current = prefillKey;
    setEditingId("");
    setForm(buildDuplicateQuotationForm(duplicateQuotationPrefill, customers));
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(location.pathname, { replace: true, state: {} });
  }, [customers, loading, location.pathname, location.state, navigate]);

  useEffect(() => {
    const quotationToEdit = location.state?.quotationEdit;
    const prefillKey = location.state?.prefillKey || "";

    if (loading || !quotationToEdit || lastAppliedPrefillKeyRef.current === prefillKey) {
      return;
    }

    lastAppliedPrefillKeyRef.current = prefillKey;
    setEditingId(quotationToEdit._id || "");
    setShowForm(true);
    setForm({
      ...emptyForm(),
      ...quotationToEdit,
      applyMarkup: Boolean(quotationToEdit.applyMarkup ?? ((quotationToEdit.markupPercent ?? 0) > 0)),
      quotationDate: quotationToEdit.quotationDate ? new Date(quotationToEdit.quotationDate).toISOString().slice(0, 10) : today(),
      validUntil: quotationToEdit.validUntil ? new Date(quotationToEdit.validUntil).toISOString().slice(0, 10) : "",
      sgst: String(quotationToEdit.sgst ?? 9),
      cgst: String(quotationToEdit.cgst ?? 9),
      igst: String(quotationToEdit.igst ?? getDefaultIntegratedGst(quotationToEdit.gstType)),
      roundOffEnabled: quotationToEdit.roundOffEnabled ? "yes" : "no",
      markupPercent: String(quotationToEdit.markupPercent ?? 10),
      overallDiscountPercent: String(quotationToEdit.overallDiscountPercent ?? ""),
      commonDiscountType: quotationToEdit.items?.every((item) => (item.discountType || "percent") === (quotationToEdit.items?.[0]?.discountType || "percent"))
        ? (quotationToEdit.items?.[0]?.discountType || "percent")
        : "percent",
      items: quotationToEdit.items?.length
        ? quotationToEdit.items.map((item) => recalculateDiscountedItem({
            itemCode: item.itemCode || "",
            hsnCode: item.hsnCode || "",
            description: item.description || "",
            quantity: String(item.quantity ?? ""),
            unit: formatUnit(item.unit),
            listPrice: String(item.listPrice ?? item.unitPrice ?? ""),
            discountType: item.discountType || "percent",
            discountValue: String(item.discountValue ?? ""),
            autoUnitPrice: "",
            isUnitPriceManual: Boolean(item.isUnitPriceManual),
            unitPrice: String(item.unitPrice ?? ""),
            totalAmount: String(item.totalAmount ?? "")
          }, quotationToEdit.markupPercent ?? 10, quotationToEdit.applyMarkup ?? ((quotationToEdit.markupPercent ?? 0) > 0)))
        : [emptyItem()]
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(location.pathname, { replace: true, state: {} });
  }, [loading, location.pathname, location.state, navigate]);

  const totals = useMemo(() => {
    const listPriceTotal = form.items.reduce((sum, item) => sum + (Number(item.listPrice || 0) * Number(item.quantity || 0)), 0);
    const subTotal = form.items.reduce((sum, item) => sum + Number(item.totalAmount || 0), 0);
    const totalDiscount = Math.max(listPriceTotal - subTotal, 0);
    const discountPercent = listPriceTotal > 0 ? ((totalDiscount / listPriceTotal) * 100).toFixed(2) : "0.00";

    const overallDiscountPercent = Math.min(Math.max(Number(form.overallDiscountPercent) || 0, 0), 100);
    const overallDiscountAmount = Number((subTotal * overallDiscountPercent / 100).toFixed(2));
    const discountedSubTotal = Number((subTotal - overallDiscountAmount).toFixed(2));

    const gstPercent = isIntegratedGstType(form.gstType)
      ? Number(form.igst || getDefaultIntegratedGst(form.gstType))
      : Number(form.sgst || 0) + Number(form.cgst || 0);
    const gstAmount = Number((discountedSubTotal * gstPercent / 100).toFixed(2));
    const calculatedGrandTotal = Number((discountedSubTotal + gstAmount).toFixed(2));
    const isRoundOff = form.roundOffEnabled === "yes";
    const grandTotal = isRoundOff ? Math.round(calculatedGrandTotal) : calculatedGrandTotal;
    const roundOffAmount = Number((grandTotal - calculatedGrandTotal).toFixed(2));

    return {
      listPriceTotal,
      subTotal,
      totalDiscount,
      discountPercent,
      overallDiscountPercent,
      overallDiscountAmount,
      discountedSubTotal,
      gstPercent,
      gstAmount,
      calculatedGrandTotal,
      roundOffAmount,
      grandTotal
    };
  }, [form]);

  const applyCustomer = (value) => {
    const matched = customers.find((customer) =>
      [customer.name, customer.customerId]
        .filter(Boolean)
        .some((entry) => entry.toLowerCase() === value.trim().toLowerCase())
    );

    setForm((current) => ({
      ...current,
      customerName: value,
      customerId: matched?._id || "",
      contactPerson: matched?.contactPerson || "",
      phone: matched?.phone || "",
      email: matched?.email || "",
      gstNumber: matched?.gstNumber || "",
      lutRefNo: matched?.lutRefNo || current.lutRefNo || "",
      billingAddress: matched ? formatAddress(matched) : current.billingAddress
    }));
  };

  const updateField = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === "customerName") {
      applyCustomer(value);
      return;
    }

    if (name === "commonDiscountType") {
      setForm((current) => {
        return {
          ...current,
          commonDiscountType: value,
          items: current.items.map((item) => recalculateDiscountedItem({
            ...item,
            discountType: value
          }, current.markupPercent, current.applyMarkup))
        };
      });
      return;
    }

    if (name === "applyMarkup") {
      setForm((current) => ({
        ...current,
        applyMarkup: checked,
        items: current.items.map((item) => recalculateDiscountedItem(item, current.markupPercent, checked))
      }));
      return;
    }

    if (name === "markupPercent") {
      setForm((current) => ({
        ...current,
        markupPercent: value,
        items: current.items.map((item) => recalculateDiscountedItem(item, value, current.applyMarkup))
      }));
      return;
    }

    if (name === "gstType") {
      setForm((current) => ({
        ...current,
        gstType: value,
        igst: normalizeGstType(value) === "sez_supply"
          ? "0"
          : current.igst || getDefaultIntegratedGst(value)
      }));
      return;
    }

    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };

  const updateItem = (index, field, value) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => {
        if (itemIndex !== index) return item;

        const next = { ...item, [field]: value };
        if (field === "unitPrice") {
          return recalculateDiscountedItem({
            ...next,
            isUnitPriceManual: true
          }, current.markupPercent, current.applyMarkup);
        }
        if (field === "discountValue") {
          return recalculateDiscountedItem({
            ...next,
            discountType: current.commonDiscountType
          }, current.markupPercent, current.applyMarkup);
        }
        if (["quantity", "listPrice"].includes(field)) {
          return recalculateDiscountedItem(next, current.markupPercent, current.applyMarkup);
        }
        return next;
      })
    }));
  };

  const switchToAutoUnitPrice = (index) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => (
        itemIndex !== index
          ? item
          : recalculateDiscountedItem({
            ...item,
            isUnitPriceManual: false
          }, current.markupPercent, current.applyMarkup)
      ))
    }));
  };

  const addItem = () => {
    setForm((current) => ({
        ...current,
        items: [
          ...current.items,
          recalculateDiscountedItem({
            ...emptyItem(),
            discountType: current.commonDiscountType
          }, current.markupPercent, current.applyMarkup)
        ]
      }));
  };

  const removeItem = (index) => {
    setForm((current) => ({
      ...current,
      items: current.items.length === 1
        ? [recalculateDiscountedItem({
          ...emptyItem(),
          discountType: current.commonDiscountType
        }, current.markupPercent, current.applyMarkup)]
        : current.items.filter((_, itemIndex) => itemIndex !== index)
    }));
  };

  const moveItem = (index, direction) => {
    setForm((current) => {
      const items = [...(current.items || [])];
      if (direction === "up" && index > 0) {
        const temp = items[index];
        items[index] = items[index - 1];
        items[index - 1] = temp;
      } else if (direction === "down" && index < items.length - 1) {
        const temp = items[index];
        items[index] = items[index + 1];
        items[index + 1] = temp;
      }
      return { ...current, items };
    });
  };

  const fillItemFromProduct = (index, product) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        const next = {
          ...item,
          itemCode:    product.itemCode    || "",
          hsnCode:     product.hsnCode     || "",
          itemName:    product.description || [product.category, product.size, product.poles, product.version].filter(Boolean).join(" | "),
          description: "",
          listPrice:   product.isOnRequest ? "" : String(product.listPrice || ""),
          unit:        formatUnit(product.unit),
          quantity:    item.quantity       || "1",
          discountType: current.commonDiscountType,
          isUnitPriceManual: false,
        };
        return recalculateDiscountedItem(next, current.markupPercent, current.applyMarkup);
      })
    }));
  };

  const resetForm = () => {
    setForm(emptyForm());
    setEditingId("");
    setShowForm(false);

    const fromPath = location.state?.fromPath;
    if (fromPath) {
      navigate(fromPath, {
        state: {
          activeSection: "quotations"
        }
      });
    }
  };

  const submitQuotation = async (event) => {
    console.log("submitQuotation event type:", event?.type, "target:", event?.target);
    console.trace("submitQuotation trace");
    event.preventDefault();

    // Combine itemName + description into a single description for storage/PDF
    const combineDescription = (item) => {
      const mat = (item.itemName || "").trim();
      const desc = (item.description || "").trim();
      return [mat, desc].filter(Boolean).join(" | ");
    };

    const items = form.items.filter((item) =>
      (item.itemName || "").trim() || (item.description || "").trim() || (item.itemCode || "").trim() || item.quantity
    );

    if (!form.customerName.trim()) {
      alert("Select or enter a customer");
      return;
    }

    if (!items.length) {
      alert("Add at least one quotation item");
      return;
    }

    const invalidIndex = items.findIndex((item) => !combineDescription(item) || Number(item.quantity) <= 0 || Number(item.unitPrice) <= 0);
    const invalidListPriceIndex = items.findIndex((item) => Number(item.listPrice) <= 0);
    if (invalidIndex >= 0) {
      alert(`Enter material/description, quantity, and valid discount details in item row ${invalidIndex + 1}`);
      return;
    }
    if (invalidListPriceIndex >= 0) {
      alert(`Enter list price in item row ${invalidListPriceIndex + 1}`);
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...form,
        applyMarkup: Boolean(form.applyMarkup),
        roundOffEnabled: form.roundOffEnabled === "yes",
        createdBy: accountsName,
        markupPercent: Number(form.markupPercent) || 0,
        overallDiscountPercent: Number(form.overallDiscountPercent) || 0,
        items: items.map((item) => ({
          ...item,
          description: combineDescription(item),
          quantity: Number(item.quantity),
          listPrice: Number(item.listPrice) || 0,
          discountType: item.discountType === "amount" ? "amount" : "percent",
          discountValue: Number(item.discountValue) || 0,
          isUnitPriceManual: Boolean(item.isUnitPriceManual),
          unitPrice: Number(item.unitPrice),
          totalAmount: Number(item.totalAmount) || Number((Number(item.quantity) * Number(item.unitPrice)).toFixed(2))
        }))
      };

      if (editingId) {
        await axios.put(API_BASE_URL + `/api/quotations/${editingId}`, payload);
      } else {
        await axios.post(API_BASE_URL + "/api/quotations", payload);
      }

      resetForm();
      await loadData(search);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to save quotation");
    } finally {
      setSaving(false);
    }
  };

  const editQuotation = (quotation) => {
    setEditingId(quotation._id);
    setShowForm(true);
    setForm({
      ...emptyForm(),
      ...quotation,
      applyMarkup: Boolean(quotation.applyMarkup ?? ((quotation.markupPercent ?? 0) > 0)),
      quotationDate: quotation.quotationDate ? new Date(quotation.quotationDate).toISOString().slice(0, 10) : today(),
      validUntil: quotation.validUntil ? new Date(quotation.validUntil).toISOString().slice(0, 10) : "",
      sgst: String(quotation.sgst ?? 9),
      cgst: String(quotation.cgst ?? 9),
      igst: String(quotation.igst ?? getDefaultIntegratedGst(quotation.gstType)),
      roundOffEnabled: quotation.roundOffEnabled ? "yes" : "no",
      markupPercent: String(quotation.markupPercent ?? 10),
      overallDiscountPercent: String(quotation.overallDiscountPercent ?? ""),
      commonDiscountType: quotation.items?.every((item) => (item.discountType || "percent") === (quotation.items?.[0]?.discountType || "percent"))
        ? (quotation.items?.[0]?.discountType || "percent")
        : "percent",
      items: quotation.items?.length
        ? quotation.items.map((item) => recalculateDiscountedItem({
            itemCode: item.itemCode || "",
            hsnCode: item.hsnCode || "",
            description: item.description || "",
            quantity: String(item.quantity ?? ""),
            unit: formatUnit(item.unit),
            listPrice: String(item.listPrice ?? item.unitPrice ?? ""),
            discountType: item.discountType || "percent",
            discountValue: String(item.discountValue ?? ""),
            autoUnitPrice: "",
            isUnitPriceManual: Boolean(item.isUnitPriceManual),
            unitPrice: String(item.unitPrice ?? ""),
            totalAmount: String(item.totalAmount ?? "")
          }, quotation.markupPercent ?? 10, quotation.applyMarkup ?? ((quotation.markupPercent ?? 0) > 0)))
        : [emptyItem()]
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteQuotation = async (quotation) => {
    if (!window.confirm(`Delete quotation ${quotation.quotationNo}?`)) return;
    await axios.delete(API_BASE_URL + `/api/quotations/${quotation._id}`);
    await loadData(search);
  };

  const openQuotationPdf = (quotation) => {
    window.open(appendAuthToken(API_BASE_URL + `/api/quotations/${quotation._id}/pdf`), "_blank", "noopener,noreferrer");
  };

  const downloadQuotationPdf = (quotation) => {
    window.open(appendAuthToken(API_BASE_URL + `/api/quotations/${quotation._id}/pdf?download=1`), "_blank", "noopener,noreferrer");
  };

  const openReQuotationDraft = (quotation) => {
    navigate(location.pathname, {
      state: {
        reQuotationPrefill: quotation,
        prefillKey: `requote-${quotation._id}-${Date.now()}`
      }
    });
  };

  const openDuplicateQuotationDraft = (quotation) => {
    navigate(location.pathname, {
      state: {
        duplicateQuotationPrefill: quotation,
        prefillKey: `duplicate-${quotation._id}-${Date.now()}`
      }
    });
  };

  const openSalesInvoiceDraft = (quotation) => {
    const salesInvoicePath = role === "admin" ? "/admin/sales-invoices" : "/accounts/sales-invoices";
    navigate(salesInvoicePath, {
      state: {
        accountDocumentPrefill: buildSalesInvoicePrefill(quotation),
        prefillKey: `sales-invoice-${quotation._id}-${Date.now()}`
      }
    });
  };

  const openPerformaInvoiceDraft = (quotation) => {
    const performaInvoicePath = role === "admin" ? "/admin/performa-invoices" : "/accounts/performa-invoices";
    navigate(performaInvoicePath, {
      state: {
        accountDocumentPrefill: buildSalesInvoicePrefill(quotation),
        prefillKey: `performa-invoice-${quotation._id}-${Date.now()}`
      }
    });
  };

  return (
    <div className="mobile-dense-page space-y-3 overflow-x-hidden p-3 bg-slate-50 min-h-screen text-slate-800">
      {/* SECTION 1: PAGE HEADER */}
      <section className="rounded-[4px] border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <nav className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">
              Accounts / Quotations
            </nav>
            <h1 className="text-lg font-bold text-slate-900 mt-0.5 flex items-center gap-1.5">
              <span className="text-blue-600">📄</span> {showForm ? (editingId ? "Edit Quotation" : "Create Quotation") : "Quotations"}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {showForm ? (
              <>
                <button
                  type="submit"
                  form="quotation-form"
                  disabled={saving}
                  className="rounded-[4px] bg-blue-600 hover:bg-blue-700 px-4 py-1.5 text-sm font-semibold text-white transition disabled:opacity-60 h-9 flex items-center justify-center gap-1.5 min-w-[130px]"
                >
                  <span>💾</span> {saving ? "Saving..." : "Save Quotation"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-[4px] border border-slate-300 bg-white hover:bg-slate-50 px-4 py-1.5 text-sm font-semibold text-slate-700 transition h-9 flex items-center justify-center gap-1.5"
                >
                  <span>✕</span> Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setEditingId("");
                  setForm(emptyForm());
                  setShowForm(true);
                }}
                className="rounded-[4px] bg-blue-600 hover:bg-blue-700 px-4 py-1.5 text-sm font-semibold text-white transition h-9 flex items-center justify-center"
              >
                + Add Quotation
              </button>
            )}
          </div>
        </div>
      </section>

      {showForm ? (
        <form id="quotation-form" onSubmit={submitQuotation} className="space-y-4">
          
          {/* Top Cards: Customer & Contact and Quotation Details */}
          <div className="grid gap-4 md:grid-cols-2">
            
            {/* SECTION 2A: CUSTOMER & CONTACT */}
            <div className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
              <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                <span className="text-blue-600 text-sm">👥</span> Customer & Contact
              </h2>
              
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-semibold text-slate-500">Customer <span className="text-red-500">*</span></label>
                  <ReactSelect
                    options={customerOptions}
                    value={selectedCustomerOption}
                    styles={reactSelectStyles}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    onChange={(option) => {
                      if (option) {
                        setForm((current) => ({
                          ...current,
                          customerName: option.label,
                          customerId: option.value,
                          contactPerson: option.data?.contactPerson || "",
                          phone: option.data?.phone || "",
                          email: option.data?.email || "",
                          gstNumber: option.data?.gstNumber || "",
                          billingAddress: option.data ? formatAddress(option.data) : current.billingAddress
                        }));
                      } else {
                        setForm((current) => ({
                          ...current,
                          customerName: "",
                          customerId: "",
                          contactPerson: "",
                          phone: "",
                          email: "",
                          gstNumber: "",
                          billingAddress: ""
                        }));
                      }
                    }}
                    placeholder="Search customer name or ID..."
                    isClearable
                  />
                </div>
                <Input label="Contact Person" name="contactPerson" value={form.contactPerson} onChange={updateField} />
                <Input label="Phone" name="phone" value={form.phone} onChange={updateField} />
                <Input label="Email" name="email" value={form.email} onChange={updateField} />
                <Input label="GST No" name="gstNumber" value={form.gstNumber} onChange={updateField} />
                <Input label="Reference No" name="referenceNo" value={form.referenceNo} onChange={updateField} />
              </div>
            </div>

            {/* SECTION 2B: QUOTATION DETAILS */}
            <div className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
              <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                <span className="text-blue-600 text-sm">📄</span> Quotation Details
              </h2>
              
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                <Input label="Quotation No" name="quotationNo" value={form.quotationNo} onChange={updateField} placeholder="Auto-generated" />
                <Input label="Quotation Date" name="quotationDate" value={form.quotationDate} onChange={updateField} type="date" />
                <Input label="Valid Until" name="validUntil" value={form.validUntil} onChange={updateField} type="date" />
                
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-500">Status</label>
                  <select 
                    name="status" 
                    value={form.status} 
                    onChange={updateField} 
                    className="w-full rounded-[4px] border border-slate-300 bg-white px-2 py-1.5 text-xs font-bold uppercase tracking-wider outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 h-9 text-blue-700 bg-blue-50/50"
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="accepted">Accepted</option>
                    <option value="rejected">Rejected</option>
                    <option value="expired">Expired</option>
                    <option value="invoiced">Invoiced</option>
                  </select>
                </div>

                <Select label="GST Type" name="gstType" value={form.gstType} onChange={updateField} options={[
                  ["cgst_sgst", "GST"],
                  ["igst", "IGST"],
                  ["sez_supply", "SEZ SUPPLY"]
                ]} />
                {isIntegratedGstType(form.gstType) ? (
                  <Input label="IGST %" name="igst" value={form.igst} onChange={updateField} type="number" min="0" step="0.01" />
                ) : (
                  <>
                    <Input label="SGST %" name="sgst" value={form.sgst} onChange={updateField} type="number" min="0" step="0.01" />
                    <Input label="CGST %" name="cgst" value={form.cgst} onChange={updateField} type="number" min="0" step="0.01" />
                  </>
                )}
                <Select label="Round Off" name="roundOffEnabled" value={form.roundOffEnabled} onChange={updateField} options={[
                  ["no", "No"],
                  ["yes", "Yes"]
                ]} />
                
                <Input label="Payment Terms" name="paymentTerms" value={form.paymentTerms} onChange={updateField} placeholder="30 Days" />
                
                <Input label="Delivery Terms" name="deliveryTime" value={form.deliveryTime} onChange={updateField} placeholder="Ex-Works" />
                
                <Input label="Warranty" name="warranty" value={form.warranty} onChange={updateField} placeholder="12 months against manufacturing defects" />
              </div>
            </div>
          </div>

          {/* SECTION 3: QUOTATION ITEMS */}
          <div className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2 mb-3">
              <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                <span className="text-blue-600 text-sm">🛒</span> Quotation Items
              </h2>
              
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={addItem}
                  className="rounded-[4px] border border-blue-300 bg-blue-50/50 hover:bg-blue-50 text-blue-700 px-3 py-1 text-xs font-semibold h-8 flex items-center gap-1 transition"
                >
                  <span>+</span> Add Item
                </button>
                <button
                  type="button"
                  className="rounded-[4px] border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 px-3 py-1 text-xs font-semibold h-8 flex items-center gap-1 transition"
                >
                  <span>📥</span> Import
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm("Clear all item rows?")) {
                      setForm((current) => ({ ...current, items: [emptyItem()] }));
                    }
                  }}
                  className="rounded-[4px] border border-red-200 bg-red-50/30 hover:bg-red-50 text-red-600 px-3 py-1 text-xs font-semibold h-8 flex items-center gap-1 transition"
                >
                  <span>🗑️</span> Clear All
                </button>
              </div>
            </div>
            
            {/* Spreadsheet settings toolbar */}
            <div className="mb-2 flex flex-wrap items-center gap-4 bg-slate-50 p-2 border border-slate-200 rounded-[4px] text-xs">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-slate-500">Discount Type:</span>
                <select
                  name="commonDiscountType"
                  value={form.commonDiscountType}
                  onChange={updateField}
                  className="rounded-[4px] border border-slate-300 bg-white px-2 py-1 text-xs outline-none h-8 text-slate-700"
                >
                  <option value="percent">Percent (%)</option>
                  <option value="amount">Amount</option>
                </select>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-slate-500">Markup %:</span>
                <input
                  name="markupPercent"
                  value={form.markupPercent}
                  onChange={updateField}
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="10.00"
                  disabled={!form.applyMarkup}
                  className="w-16 rounded-[4px] border border-slate-300 bg-white px-2 py-1 text-xs outline-none disabled:bg-slate-100 disabled:text-slate-400 h-8 text-slate-700 text-right"
                />
              </div>
              <div className="flex items-center gap-1 text-[11px] text-slate-500 italic">
                <span className="text-blue-500 font-bold">ⓘ</span>
                <span>Unit Price auto-calculates from List Price.</span>
              </div>
            </div>

            {/* Grid-based spreadsheet table */}
            <div className="max-w-full overflow-x-auto pb-1">
              <div className="min-w-[1200px] overflow-visible rounded-[4px] border border-slate-200 bg-white">
                {/* Header row */}
                <div className="grid bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-600" style={{gridTemplateColumns: "40px 140px 100px 1fr 80px 80px 120px 120px 130px 120px 120px"}}>
                  <span className="p-2 text-center border-r border-slate-200">#</span>
                  <span className="p-2 border-r border-slate-200">Item Code</span>
                  <span className="p-2 border-r border-slate-200">HSN Code</span>
                  <span className="p-2 border-r border-slate-200">Description</span>
                  <span className="p-2 text-right border-r border-slate-200">Qty</span>
                  <span className="p-2 border-r border-slate-200">Unit</span>
                  <span className="p-2 text-right border-r border-slate-200">List Price</span>
                  <span className="p-2 text-right border-r border-slate-200">Discount</span>
                  <span className="p-2 text-right border-r border-slate-200">Unit Price</span>
                  <span className="p-2 text-right border-r border-slate-200">Total</span>
                  <span className="p-2 text-center">Action</span>
                </div>

                {/* Rows */}
                {form.items.map((item, index) => (
                  <div key={`quotation-item-row-${index}`} className="grid border-b border-slate-200 hover:bg-slate-50/40 items-stretch" style={{gridTemplateColumns: "40px 140px 100px 1fr 80px 80px 120px 120px 130px 120px 120px"}}>
                    <div className="p-1 border-r border-slate-200 flex items-center justify-center text-xs font-semibold text-slate-500">
                      {index + 1}
                    </div>
                    <div className="p-1 border-r border-slate-200 flex items-center">
                      <div className="flex items-center gap-1 w-full relative">
                        <TableInput value={item.itemCode} onChange={(value) => updateItem(index, "itemCode", value)} placeholder="Code" className="font-mono text-xs uppercase" />
                        <ProductPicker onSelect={(product) => fillItemFromProduct(index, product)} compact />
                      </div>
                    </div>
                    <div className="p-1 border-r border-slate-200 flex items-center">
                      <HsnCodeInput value={item.hsnCode} onChange={(value) => updateItem(index, "hsnCode", value)} index={index} />
                    </div>
                    <div className="p-1 border-r border-slate-200 flex flex-col justify-center">
                      <div className="rounded-[4px] border border-slate-200 overflow-hidden bg-white">
                        <input
                          value={item.itemName}
                          onChange={(e) => updateItem(index, "itemName", e.target.value)}
                          placeholder="Material"
                          className="w-full border-0 border-b border-slate-200 bg-white px-2 py-1 text-xs outline-none focus:bg-slate-50"
                        />
                        <input
                          value={item.description}
                          onChange={(e) => updateItem(index, "description", e.target.value)}
                          placeholder="Description"
                          className="w-full border-0 bg-white px-2 py-1 text-xs outline-none focus:bg-slate-50"
                        />
                      </div>
                    </div>
                    <div className="p-1 border-r border-slate-200 flex items-center">
                      <TableInput value={item.quantity} onChange={(value) => updateItem(index, "quantity", value)} type="number" min="0.01" step="0.01" placeholder="0" className="text-right font-medium" />
                    </div>
                    <div className="p-1 border-r border-slate-200 flex items-center">
                      <select
                        value={formatUnit(item.unit)}
                        onChange={(event) => updateItem(index, "unit", event.target.value)}
                        className="w-full rounded-[4px] border border-slate-200 bg-white px-1 h-8 text-xs outline-none focus:border-blue-500 font-medium"
                      >
                        {unitOptions.map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="p-1 border-r border-slate-200 flex items-center">
                      <div className="flex items-center bg-white px-2 border border-slate-200 rounded-[4px] h-8 focus-within:border-blue-500 w-full">
                        <span className="text-slate-400 mr-0.5 text-xs">₹</span>
                        <input
                          value={item.listPrice}
                          onChange={(e) => updateItem(index, "listPrice", e.target.value)}
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          className="w-full border-0 p-0 text-right outline-none text-xs text-slate-800 font-medium"
                        />
                      </div>
                    </div>
                    <div className="p-1 border-r border-slate-200 flex items-center">
                      <div className="flex items-center bg-white px-2 border border-slate-200 rounded-[4px] h-8 focus-within:border-blue-500 w-full">
                        <input
                          value={item.discountValue}
                          onChange={(event) => updateItem(index, "discountValue", event.target.value)}
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          className="w-full border-0 p-0 text-right outline-none text-xs text-slate-800 font-medium"
                        />
                        <span className="text-slate-400 ml-1 text-xs">
                          {form.commonDiscountType === "amount" ? "" : "%"}
                        </span>
                      </div>
                    </div>
                    <div className="p-1 border-r border-slate-200 flex items-center">
                      <div className="w-full">
                        <div className="flex items-center bg-white px-2 border border-slate-200 rounded-[4px] h-8 focus-within:border-blue-500 w-full">
                          <span className="text-slate-400 mr-0.5 text-xs">₹</span>
                          <input
                            value={item.unitPrice}
                            onChange={(e) => updateItem(index, "unitPrice", e.target.value)}
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            className="w-full border-0 p-0 text-right outline-none text-xs text-slate-800 font-medium"
                          />
                        </div>
                        <div className="mt-0.5 flex items-center justify-between gap-1 text-[9px] px-1 text-slate-400">
                          <span>Auto: {toDecimalString(item.autoUnitPrice) || "0.00"}</span>
                          {item.isUnitPriceManual ? (
                            <button
                              type="button"
                              onClick={() => switchToAutoUnitPrice(index)}
                              className="text-blue-600 hover:underline font-semibold"
                            >
                              Auto
                            </button>
                          ) : (
                            <span className="text-emerald-600 font-semibold">Auto</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="p-1 border-r border-slate-200 flex items-center">
                      <div className="flex items-center bg-slate-50 px-2 border border-slate-200 rounded-[4px] h-8 w-full">
                        <span className="text-slate-400 mr-0.5 text-xs font-bold">₹</span>
                        <input
                          value={item.totalAmount}
                          onChange={() => {}}
                          type="number"
                          min="0"
                          step="0.01"
                          readOnly
                          placeholder="0.00"
                          className="w-full border-0 p-0 text-right outline-none text-xs text-slate-900 bg-slate-50 font-bold"
                        />
                      </div>
                    </div>
                    <div className="p-1 flex items-center justify-center">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          disabled={index === 0}
                          onClick={() => moveItem(index, "up")}
                          className="text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400 transition"
                          title="Move row up"
                        >
                          <span className="text-xs">▲</span>
                        </button>
                        <button
                          type="button"
                          disabled={index === form.items.length - 1}
                          onClick={() => moveItem(index, "down")}
                          className="text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400 transition"
                          title="Move row down"
                        >
                          <span className="text-xs">▼</span>
                        </button>
                        <button type="button" className="text-slate-400 hover:text-slate-700 transition" title="Edit row">
                          <span className="text-xs">✏️</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-red-400 hover:text-red-600 transition"
                          title="Remove row"
                        >
                          <span className="text-xs">🗑️</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {form.items.length === 0 && (
                  <div className="py-8 text-center text-sm text-slate-400">No items yet. Click + Add Item to begin.</div>
                )}

                {/* Add New Row button centered inside container */}
                <div className="p-2 border-t border-slate-200 bg-white flex justify-center">
                  <button
                    type="button"
                    onClick={addItem}
                    className="w-full max-w-xs border border-dashed border-blue-300 rounded-[4px] py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-50/50 transition flex items-center justify-center gap-1"
                  >
                    <span>+</span> Add New Row
                  </button>
                </div>

                {/* Bottom Spreadsheet Info Bar */}
                <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs font-bold text-slate-600">
                  <div>Total Items: {form.items.length}</div>
                  <div className="flex items-center gap-8">
                    <span>Sub Total (Before Tax)</span>
                    <span className="font-bold text-slate-800">{formatCurrency(totals.subTotal)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Area: Grid for Address & Notes (left) + Summary & Settings (right) */}
          <div className="grid gap-4 lg:grid-cols-12 items-start">
            
            {/* Left Column: Billing Address & Notes / Terms */}
            <div className="lg:col-span-8 space-y-4">
              <div className="grid gap-4 md:grid-cols-5 items-stretch">
                
                {/* SECTION 5A: BILLING ADDRESS */}
                <div className="md:col-span-2 rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
                  <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                    <span className="text-blue-600 text-sm">📍</span> Billing Address
                  </h2>
                  <div>
                    <textarea 
                      name="billingAddress" 
                      value={form.billingAddress} 
                      onChange={updateField} 
                      rows={4} 
                      placeholder="Enter customer billing address details here..."
                      className="w-full rounded-[4px] border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white resize-y min-h-[100px] text-slate-700" 
                    />
                  </div>
                </div>

                {/* SECTION 5B: NOTES / TERMS */}
                <div className="md:col-span-3 rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
                  <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                    <span className="text-blue-600 text-sm">📝</span> Notes / Terms
                  </h2>
                  <div className="space-y-3">
                    <Input label="Subject" name="subject" value={form.subject} onChange={updateField} placeholder="Supply of materials / service quotation" />
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-slate-500">Terms & Conditions</label>
                        <textarea name="terms" value={form.terms} onChange={updateField} rows={2} className="w-full rounded-[4px] border border-slate-300 px-2 py-1.5 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white resize-y h-[70px] text-slate-700" />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-slate-500">Notes / Remarks</label>
                        <textarea name="notes" value={form.notes} onChange={updateField} rows={2} className="w-full rounded-[4px] border border-slate-300 px-2 py-1.5 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white resize-y h-[70px] text-slate-700" />
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Right Column: Quotation Summary & Settings */}
            <div className="lg:col-span-4 space-y-4">
              
              {/* SECTION 4: QUOTATION SUMMARY */}
              <div className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
                <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                  <span className="text-blue-600 text-sm">📊</span> Quotation Summary
                </h2>
                <div className="space-y-2">
                  <SummaryRow label="Sub Total" value={formatCurrency(totals.listPriceTotal)} strong={true} />
                  
                  <div className="flex items-center justify-between rounded-[4px] bg-white px-3 py-2 border border-slate-200 text-sm text-red-600 font-semibold">
                    <span>Discount ( % )</span>
                    <span>-{formatCurrency(totals.totalDiscount)}</span>
                  </div>

                  <SummaryRow label="After Discount" value={formatCurrency(totals.subTotal)} />

                  {/* Overall Discount Input */}
                  <div className="flex items-center justify-between rounded-[4px] bg-red-50/40 px-3 py-2 border border-red-200 text-sm text-red-700 font-semibold gap-2">
                    <span className="whitespace-nowrap">Overall Disc.</span>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={form.overallDiscountPercent}
                        onChange={(e) => setForm((prev) => ({ ...prev, overallDiscountPercent: e.target.value }))}
                        placeholder="0"
                        className="w-16 rounded-[4px] border border-red-300 px-2 py-0.5 text-xs text-right outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 bg-white font-bold text-red-700"
                      />
                      <span className="text-xs font-bold">%</span>
                      {totals.overallDiscountAmount > 0 && (
                        <span className="text-xs font-bold ml-1">= -{formatCurrency(totals.overallDiscountAmount)}</span>
                      )}
                    </div>
                  </div>

                  {totals.overallDiscountAmount > 0 && (
                    <SummaryRow label="After Overall Discount" value={formatCurrency(totals.discountedSubTotal)} />
                  )}

                  <SummaryRow label={`GST (${totals.gstPercent.toFixed(2)}%)`} value={formatCurrency(totals.gstAmount)} />
                  {form.roundOffEnabled === "yes" && (
                    <SummaryRow label="Round Off" value={formatCurrency(totals.roundOffAmount)} />
                  )}
                  <SummaryRow label="Others / OH" value={formatCurrency(0)} />
                  
                  <div className="flex items-center justify-between rounded-[4px] bg-blue-50/40 px-3 py-2.5 border border-blue-200 font-bold text-blue-700 text-base">
                    <span>Grand Total</span>
                    <span>{formatCurrency(totals.grandTotal)}</span>
                  </div>
                </div>
              </div>

              {/* Checkbox Settings Area */}
              <div className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
                <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                  <span className="text-blue-600 text-sm">⚙️</span> Settings
                </h2>
                <div className="flex flex-col gap-2">
                  <CheckboxInput
                    label="Show Discount In PDF"
                    name="showDiscountInPdf"
                    checked={Boolean(form.showDiscountInPdf)}
                    onChange={updateField}
                  />
                  <CheckboxInput
                    label="Show Original Quote Ref In PDF"
                    name="showOriginalReferenceInPdf"
                    checked={Boolean(form.showOriginalReferenceInPdf)}
                    onChange={updateField}
                  />
                  <CheckboxInput
                    label="Apply Markup To Unit Price"
                    name="applyMarkup"
                    checked={Boolean(form.applyMarkup)}
                    onChange={updateField}
                  />
                </div>
              </div>

            </div>

          </div>

        </form>
      ) : null}

      {/* SECTION 6: QUOTATION HISTORY */}
      <section className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        {/* Tab switchers at the top of the history section */}
        <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mb-4">
          <button
            type="button"
            onClick={() => setQuotationTab("quotations")}
            className={`rounded-2xl px-4 py-2.5 text-xs font-semibold transition ${
              quotationTab === "quotations"
                ? "bg-sky-600 text-white shadow-sm border-sky-600"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Quotations
          </button>
          <button
            type="button"
            onClick={() => setQuotationTab("products")}
            className={`rounded-2xl px-4 py-2.5 text-xs font-semibold transition ${
              quotationTab === "products"
                ? "bg-sky-600 text-white shadow-sm border-sky-600"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Products
          </button>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-slate-100 pb-3">
          <div>
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
              <span className="text-blue-600 text-sm">{quotationTab === "quotations" ? "📜" : "🛒"}</span> {quotationTab === "quotations" ? "Quotation History" : "Quoted Products History"}
            </h2>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input 
              value={search} 
              onChange={(event) => {
                setSearch(event.target.value);
                setQuotationsLimit(20);
                setProductsLimit(20);
              }} 
              placeholder="Search quotations or products..." 
              className="rounded-[4px] border border-slate-300 px-3 py-1 text-sm outline-none focus:border-blue-500 h-9 bg-white w-full sm:w-64 text-slate-700" 
            />
            <button 
              type="button" 
              onClick={() => loadData(search)} 
              className="rounded-[4px] bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold px-4 h-9 transition shrink-0"
            >
              Search
            </button>
          </div>
        </div>

        {quotationTab === "quotations" ? (
          <>
            {/* Mobile Grid Layout for History */}
            <div className="mt-4 space-y-3 md:hidden">
              {loading ? (
                <div className="rounded-[4px] border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-400">Loading quotations...</div>
              ) : displayedQuotations.length === 0 ? (
                <div className="rounded-[4px] border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-400">No quotations found.</div>
              ) : displayedQuotations.map((quotation) => (
                <article key={`mobile-${quotation._id}`} className="rounded-[4px] border border-slate-200 bg-white p-3 shadow-sm space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{formatDate(quotation.quotationDate)}</p>
                      <h3 className="mt-0.5 break-words text-sm font-bold text-slate-900">{quotation.quotationNo || "Quotation"}</h3>
                      <button
                        type="button"
                        onClick={() => {
                          if (quotation.customerId) {
                            navigate(role === "accounts" ? `/accounts/customer/${quotation.customerId}` : `/customer/${quotation.customerId}`);
                          }
                        }}
                        className="mt-0.5 truncate text-xs text-blue-600 hover:text-blue-800 hover:underline font-semibold text-left block"
                        disabled={!quotation.customerId}
                      >
                        {quotation.customerName || "-"}
                      </button>
                    </div>
                    <select
                      value={quotation.status || "draft"}
                      onChange={(e) => updateStatus(quotation._id, e.target.value)}
                      className={`shrink-0 inline-flex items-center rounded-[4px] border px-1 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-transparent outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer ${
                        quotation.status === "accepted" || quotation.status === "invoiced" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                        quotation.status === "sent" ? "bg-blue-50 text-blue-700 border-blue-200" :
                        quotation.status === "rejected" ? "bg-red-50 text-red-700 border-red-200" :
                        quotation.status === "expired" ? "bg-amber-50 text-amber-700 border-amber-200" :
                        "bg-slate-50 text-slate-600 border-slate-200"
                      }`}
                    >
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="accepted">Accepted</option>
                      <option value="rejected">Rejected</option>
                      <option value="expired">Expired</option>
                      <option value="invoiced">Invoiced</option>
                    </select>
                  </div>
                  <p className="line-clamp-2 text-xs leading-relaxed text-slate-500">{quotation.subject || "-"}</p>
                  <div className="grid grid-cols-2 gap-2 rounded-[4px] bg-slate-50 p-2.5 text-xs border border-slate-100">
                    <div>
                      <p className="font-semibold text-[9px] uppercase tracking-wider text-slate-400">Valid Until</p>
                      <p className="mt-0.5 font-semibold text-slate-700">{formatDate(quotation.validUntil)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[9px] uppercase tracking-wider text-slate-400">Total</p>
                      <p className="mt-0.5 font-bold text-slate-900">{formatCurrency(quotation.grandTotal)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 pt-1">
                    <button type="button" onClick={() => setViewingQuotation(quotation)} className="rounded-[4px] border border-slate-300 bg-white hover:bg-slate-50 py-1.5 text-xs font-semibold text-slate-700 transition">
                      View
                    </button>
                    <button type="button" onClick={() => openDuplicateQuotationDraft(quotation)} className="rounded-[4px] border border-violet-200 bg-violet-50 hover:bg-violet-100 py-1.5 text-xs font-semibold text-violet-700 transition">
                      Duplicate
                    </button>
                    <button type="button" onClick={() => openQuotationPdf(quotation)} className="rounded-[4px] border border-slate-300 bg-white hover:bg-slate-50 py-1.5 text-xs font-semibold text-slate-700 transition">
                      PDF
                    </button>
                    <button type="button" onClick={() => downloadQuotationPdf(quotation)} className="rounded-[4px] border border-slate-300 bg-white hover:bg-slate-50 py-1.5 text-xs font-semibold text-slate-700 transition">
                      Download PDF
                    </button>
                    <button type="button" onClick={() => editQuotation(quotation)} className="rounded-[4px] border border-slate-300 bg-white hover:bg-slate-50 py-1.5 text-xs font-semibold text-slate-700 transition">
                      Edit
                    </button>
                    <button type="button" onClick={() => openReQuotationDraft(quotation)} className="rounded-[4px] border border-blue-200 bg-blue-50 hover:bg-blue-100 py-1.5 text-xs font-semibold text-blue-700 transition">
                      Re-Quote
                    </button>
                    <button type="button" onClick={() => openSalesInvoiceDraft(quotation)} className="rounded-[4px] border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 py-1.5 text-xs font-semibold text-emerald-700 transition">
                      Invoice
                    </button>
                    <button type="button" onClick={() => openPerformaInvoiceDraft(quotation)} className="rounded-[4px] border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 py-1.5 text-xs font-semibold text-indigo-700 transition">
                      Performa
                    </button>
                  </div>
                </article>
              ))}
            </div>

            {/* Desktop Spreadsheet-Style Table for History */}
            <div className="mt-3 hidden max-w-full overflow-x-auto md:block">
              <table className="w-full min-w-[1040px] border-collapse text-left text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
                    <th className="px-3 py-2 font-semibold border-r border-slate-100">Date</th>
                    <th className="px-3 py-2 font-semibold border-r border-slate-100">Quotation No</th>
                    <th className="px-3 py-2 font-semibold border-r border-slate-100">Customer</th>
                    <th className="px-3 py-2 font-semibold border-r border-slate-100">Subject</th>
                    <th className="px-3 py-2 font-semibold border-r border-slate-100">Valid Until</th>
                    <th className="px-3 py-2 font-semibold text-right border-r border-slate-100">Total</th>
                    <th className="px-3 py-2 font-semibold border-r border-slate-100">Status</th>
                    <th className="px-3 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="8" className="px-3 py-8 text-center text-slate-400">Loading quotations...</td></tr>
                  ) : displayedQuotations.length === 0 ? (
                    <tr><td colSpan="8" className="px-3 py-8 text-center text-slate-400">No quotations found.</td></tr>
                  ) : displayedQuotations.map((quotation) => {
                    const familyKey = getFamilyKeyAndVersion(quotation, quotations);
                    const familyVersions = groupedFamilies[familyKey] || [];
                    const isExpanded = Boolean(expandedRows[familyKey]);

                    return (
                      <React.Fragment key={quotation._id}>
                        <tr className="border-b border-slate-200 hover:bg-slate-50/50 align-middle">
                          <td className="px-3 py-2 text-slate-600 border-r border-slate-100">{formatDate(quotation.quotationDate)}</td>
                          <td className="px-3 py-2 font-semibold text-slate-900 border-r border-slate-100 font-mono">
                            <div>{quotation.quotationNo}</div>
                            {quotation.sourceQuotationNo && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedRows((prev) => ({
                                    ...prev,
                                    [familyKey]: !prev[familyKey]
                                  }));
                                }}
                                className="mt-1 flex items-center gap-0.5 text-[10px] text-blue-600 hover:text-blue-800 font-bold transition hover:underline bg-transparent border-0 p-0 cursor-pointer text-left font-sans"
                              >
                                <span>↩</span> Rev. of {quotation.sourceQuotationNo}
                              </button>
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-700 border-r border-slate-100 font-medium">
                            {quotation.customerId ? (
                              <button
                                type="button"
                                onClick={() => navigate(role === "accounts" ? `/accounts/customer/${quotation.customerId}` : `/customer/${quotation.customerId}`)}
                                className="text-blue-600 hover:text-blue-850 hover:underline font-semibold text-left"
                              >
                                {quotation.customerName || "-"}
                              </button>
                            ) : (
                              quotation.customerName || "-"
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-600 border-r border-slate-100">{quotation.subject || "-"}</td>
                          <td className="px-3 py-2 text-slate-600 border-r border-slate-100">{formatDate(quotation.validUntil)}</td>
                          <td className="px-3 py-2 text-right font-bold text-slate-900 border-r border-slate-100">{formatCurrency(quotation.grandTotal)}</td>
                          <td className="px-3 py-2 border-r border-slate-100">
                            <div className="relative inline-flex items-center">
                              <span className={`absolute left-2 w-1.5 h-1.5 rounded-full ${
                                quotation.status === "accepted" || quotation.status === "invoiced" ? "bg-emerald-500" :
                                quotation.status === "sent" ? "bg-blue-500" :
                                quotation.status === "rejected" ? "bg-red-500" :
                                quotation.status === "expired" ? "bg-amber-500" :
                                "bg-slate-400"
                              }`} />
                              <select
                                value={quotation.status || "draft"}
                                onChange={(e) => updateStatus(quotation._id, e.target.value)}
                                className={`inline-flex items-center rounded-full border pl-5 pr-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-transparent outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer ${
                                  quotation.status === "accepted" || quotation.status === "invoiced" ? "bg-emerald-50/70 text-emerald-700 border-emerald-200" :
                                  quotation.status === "sent" ? "bg-blue-50/70 text-blue-700 border-blue-200" :
                                  quotation.status === "rejected" ? "bg-red-50/70 text-red-700 border-red-200" :
                                  quotation.status === "expired" ? "bg-amber-50/70 text-amber-700 border-amber-200" :
                                  "bg-slate-50 text-slate-650 border-slate-200"
                                }`}
                              >
                                <option value="draft">Draft</option>
                                <option value="sent">Sent</option>
                                <option value="accepted">Accepted</option>
                                <option value="rejected">Rejected</option>
                                <option value="expired">Expired</option>
                                <option value="invoiced">Invoiced</option>
                              </select>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              {/* View Button */}
                              <button
                                type="button"
                                onClick={() => setViewingQuotation(quotation)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-blue-600 bg-white hover:bg-slate-50 border border-slate-200 rounded-md transition shadow-sm"
                              >
                                <span className="material-symbols-outlined text-[15px] text-blue-500">visibility</span>
                                <span>View</span>
                              </button>

                              {/* Edit Button */}
                              <button
                                type="button"
                                onClick={() => editQuotation(quotation)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-blue-600 bg-white hover:bg-slate-50 border border-slate-200 rounded-md transition shadow-sm"
                              >
                                <span className="material-symbols-outlined text-[15px] text-blue-500">edit</span>
                                <span>Edit</span>
                              </button>

                              {/* Sales Invoice (conditionally shown if not invoiced) */}
                              {quotation.status !== "invoiced" && (
                                <button
                                  type="button"
                                  onClick={() => openSalesInvoiceDraft(quotation)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50/60 hover:bg-emerald-100 border border-emerald-200 rounded-md transition shadow-sm"
                                >
                                  <span className="material-symbols-outlined text-[15px] text-emerald-600">assignment_turned_in</span>
                                  <span>Sales Invoice</span>
                                </button>
                              )}

                              {/* Ellipsis Dropdown */}
                              <div className="relative inline-block text-left">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDropdownId(activeDropdownId === quotation._id ? null : quotation._id);
                                  }}
                                  className="flex items-center justify-center p-1 text-slate-500 hover:text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-md transition shadow-sm h-[28px] w-[28px] focus:outline-none"
                                >
                                  <span className="material-symbols-outlined text-[18px]">more_vert</span>
                                </button>

                                {activeDropdownId === quotation._id && (
                                  <div className="absolute right-0 mt-1.5 w-48 bg-white border border-slate-200 rounded-xl shadow-lg z-50 py-1.5">
                                    <button
                                      type="button"
                                      onClick={() => openQuotationPdf(quotation)}
                                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 font-medium transition text-left"
                                    >
                                      <span className="material-symbols-outlined text-slate-400 text-[16px]">description</span>
                                      <span>PDF</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => downloadQuotationPdf(quotation)}
                                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 font-medium transition text-left"
                                    >
                                      <span className="material-symbols-outlined text-slate-400 text-[16px]">download</span>
                                      <span>Download PDF</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => openDuplicateQuotationDraft(quotation)}
                                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 font-medium transition text-left"
                                    >
                                      <span className="material-symbols-outlined text-slate-400 text-[16px]">content_copy</span>
                                      <span>Duplicate</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => openPerformaInvoiceDraft(quotation)}
                                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 font-medium transition text-left"
                                    >
                                      <span className="material-symbols-outlined text-slate-400 text-[16px]">description</span>
                                      <span>Proforma Invoice</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => openReQuotationDraft(quotation)}
                                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 font-medium transition text-left"
                                    >
                                      <span className="material-symbols-outlined text-slate-400 text-[16px]">sync</span>
                                      <span>Re-Quotation</span>
                                    </button>
                                    <div className="border-t border-slate-100 my-1"></div>
                                    <button
                                      type="button"
                                      onClick={() => deleteQuotation(quotation)}
                                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-650 hover:bg-red-50/50 font-medium transition text-left"
                                    >
                                      <span className="material-symbols-outlined text-red-500 text-[16px]">delete</span>
                                      <span>Delete</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr className="bg-slate-50/10">
                            <td colSpan="8" className="px-6 py-4 border-b border-slate-200">
                              <div className="pl-6 pr-4 space-y-3">
                                <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white shadow-sm">
                                  <table className="w-full text-left text-xs border-collapse">
                                    <thead>
                                      <tr className="bg-slate-50 border-b border-slate-200 text-[10px] text-slate-500 uppercase tracking-widest font-bold">
                                        <th className="w-12"></th>
                                        <th className="p-3">Version</th>
                                        <th className="p-3">Version Date</th>
                                        <th className="p-3">Valid Until</th>
                                        <th className="p-3">Total</th>
                                        <th className="p-3">Status</th>
                                        <th className="p-3">Created By</th>
                                        <th className="p-3 text-center">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                      {familyVersions.map((hist, idx) => {
                                        const isLatest = idx === 0;
                                        const isOriginal = idx === familyVersions.length - 1;
                                        const isLastLine = idx === familyVersions.length - 1;

                                        let subBadge = "bg-slate-50 text-slate-600 border-slate-200";
                                        if (["accepted", "invoiced"].includes(hist.status)) {
                                          subBadge = "bg-emerald-50 text-emerald-700 border-emerald-150";
                                        } else if (hist.status === "sent") {
                                          subBadge = "bg-blue-50 text-blue-700 border-blue-150";
                                        } else if (hist.status === "rejected") {
                                          subBadge = "bg-rose-50 text-rose-700 border-rose-150";
                                        } else if (hist.status === "expired") {
                                          subBadge = "bg-amber-50 text-amber-700 border-amber-150";
                                        }

                                        const rMatch = hist.quotationNo.match(/-R(\d+)$/);
                                        const versionLabel = rMatch ? `R${rMatch[1]}` : "Original";

                                        return (
                                          <tr key={hist._id} className="align-middle hover:bg-slate-50/30">
                                            {/* Left branch vertical timeline */}
                                            <td className="w-12 py-2 text-center relative">
                                              {/* Line */}
                                              {!isLastLine && (
                                                <div 
                                                  className="absolute top-4.5 bottom-0 w-0.5 border-l-2 border-dashed border-slate-300"
                                                  style={{ left: "50%", transform: "translateX(-50%)" }}
                                                ></div>
                                              )}
                                              {/* Dot circle */}
                                              {isLatest ? (
                                                <div className="mx-auto w-3.5 h-3.5 rounded-full border-2 border-blue-600 bg-white flex items-center justify-center ring-2 ring-blue-100 z-10 relative">
                                                  <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>
                                                </div>
                                              ) : (
                                                <div className="mx-auto w-2.5 h-2.5 rounded-full border border-dashed border-slate-400 bg-white z-10 relative"></div>
                                              )}
                                            </td>

                                            <td className="p-3">
                                              <span className={`font-bold ${isLatest ? "text-emerald-750 bg-emerald-50/70 px-2 py-0.5 rounded text-[10px]" : "text-slate-650 bg-slate-50 px-2 py-0.5 rounded text-[10px]"}`}>
                                                {versionLabel} {isLatest ? "(Latest)" : isOriginal ? "(Original)" : ""}
                                              </span>
                                            </td>
                                            <td className="p-3 text-slate-500 font-semibold">{formatDate(hist.quotationDate)}</td>
                                            <td className="p-3 text-slate-500 font-medium">{formatDate(hist.validUntil)}</td>
                                            <td className="p-3 font-extrabold text-slate-900">{formatCurrency(hist.grandTotal)}</td>
                                            <td className="p-3">
                                              <span className={`px-2 py-0.5 rounded border text-[9px] font-black uppercase tracking-wider ${subBadge}`}>
                                                {hist.status}
                                              </span>
                                            </td>
                                            <td className="p-3 text-slate-550 font-bold">{hist.createdBy || "Genmech Admin"}</td>
                                            <td className="p-3 text-center">
                                              <div className="flex items-center justify-center gap-1">
                                                <button
                                                  type="button"
                                                  onClick={() => setViewingQuotation(hist)}
                                                  className="h-7 px-2 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-semibold text-[11px] flex items-center gap-1 transition shadow-sm"
                                                >
                                                  View
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => openQuotationPdf(hist)}
                                                  className="h-7 px-2 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-semibold text-[11px] flex items-center gap-1 transition shadow-sm"
                                                >
                                                  PDF
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => downloadQuotationPdf(hist)}
                                                  className="h-7 px-2 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-semibold text-[11px] flex items-center gap-1 transition shadow-sm"
                                                >
                                                  Download PDF
                                                </button>
                                                {isLatest ? (
                                                  <>
                                                    <button
                                                      type="button"
                                                      onClick={() => openSalesInvoiceDraft(hist)}
                                                      className="h-7 px-2.5 rounded border border-emerald-250 bg-emerald-50/20 hover:bg-emerald-55 text-emerald-700 font-bold text-[10px] transition shadow-sm"
                                                    >
                                                      Sales Invoice
                                                    </button>
                                                    <button
                                                      type="button"
                                                      onClick={() => openPerformaInvoiceDraft(hist)}
                                                      className="h-7 px-2.5 rounded border border-indigo-200 bg-indigo-50/20 hover:bg-indigo-100 text-indigo-700 font-bold text-[10px] transition shadow-sm"
                                                    >
                                                      Performa Invoice
                                                    </button>
                                                  </>
                                                ) : (
                                                  <button
                                                    type="button"
                                                    onClick={() => openReQuotationDraft(hist)}
                                                    className="h-7 px-2.5 rounded border border-blue-200 bg-blue-50/20 hover:bg-blue-55 text-blue-700 font-bold text-[10px] transition shadow-sm"
                                                  >
                                                    Re-Quotation
                                                  </button>
                                                )}
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                                <div className="flex items-center gap-1 text-[10px] text-slate-400 font-semibold pl-2">
                                  <span className="material-symbols-outlined text-sm text-slate-400">info</span>
                                  <span>{familyVersions.length} version(s) in this quotation</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination View More for Quotations */}
            {filteredQuotations.length > quotationsLimit && (
              <div className="mt-4 flex justify-center pb-2">
                <button
                  type="button"
                  onClick={() => setQuotationsLimit((prev) => prev + 20)}
                  className="rounded-[4px] bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-4 py-2 transition"
                >
                  View More
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Mobile Grid Layout for Quoted Products */}
            <div className="mt-4 space-y-3 md:hidden">
              {displayedProducts.length === 0 ? (
                <div className="rounded-[4px] border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-400">No quoted products found.</div>
              ) : displayedProducts.map((p, idx) => (
                <article key={`mobile-prod-${p.quotationId}-${idx}`} className="rounded-[4px] border border-slate-200 bg-white p-3 shadow-sm space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{formatDate(p.quotationDate)}</p>
                      <button
                        type="button"
                        onClick={() => setViewingQuotation(p.originalQuotation)}
                        className="mt-0.5 break-words text-sm font-bold text-blue-600 hover:text-blue-800 text-left block"
                      >
                        {p.quotationNo}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (p.customerId) {
                            navigate(role === "accounts" ? `/accounts/customer/${p.customerId}` : `/customer/${p.customerId}`);
                          }
                        }}
                        className="mt-0.5 truncate text-xs text-blue-600 hover:text-blue-800 hover:underline font-semibold text-left block"
                        disabled={!p.customerId}
                      >
                        {p.customerName}
                      </button>
                    </div>
                  </div>
                  <p className="line-clamp-2 text-xs leading-relaxed text-slate-900 font-medium">{p.productName}</p>
                  <div className="grid grid-cols-2 gap-2 rounded-[4px] bg-slate-50 p-2.5 text-xs border border-slate-100">
                    <div>
                      <p className="font-semibold text-[9px] uppercase tracking-wider text-slate-400">Qty / Unit</p>
                      <p className="mt-0.5 font-semibold text-slate-700">{p.quantity} {p.unit}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[9px] uppercase tracking-wider text-slate-400">Total Price</p>
                      <p className="mt-0.5 font-bold text-slate-900">₹{(p.totalPrice || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Desktop Spreadsheet-Style Table for Quoted Products */}
            <div className="mt-3 hidden max-w-full overflow-x-auto md:block">
              <table className="w-full min-w-[1040px] border-collapse text-left text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
                    <th className="px-3 py-2 font-semibold border-r border-slate-100">Quotation Date</th>
                    <th className="px-3 py-2 font-semibold border-r border-slate-100">Quotation Number</th>
                    <th className="px-3 py-2 font-semibold border-r border-slate-100">Customer</th>
                    <th className="px-3 py-2 font-semibold border-r border-slate-100">Product Name</th>
                    <th className="px-3 py-2 font-semibold border-r border-slate-100">Item Code</th>
                    <th className="px-3 py-2 font-semibold border-r border-slate-100">HSN Code</th>
                    <th className="px-3 py-2 font-semibold text-right border-r border-slate-100">Quantity</th>
                    <th className="px-3 py-2 border-r border-slate-100">Unit</th>
                    <th className="px-3 py-2 text-right border-r border-slate-100">Unit Price</th>
                    <th className="px-3 py-2 text-right">Total Price</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedProducts.length === 0 ? (
                    <tr><td colSpan="10" className="px-3 py-8 text-center text-slate-400">No quoted products found.</td></tr>
                  ) : displayedProducts.map((p, idx) => (
                    <tr key={`prod-row-${p.quotationId}-${idx}`} className="border-b border-slate-200 hover:bg-slate-50/50 align-middle">
                      <td className="px-3 py-2 text-slate-600 border-r border-slate-100">{formatDate(p.quotationDate)}</td>
                      <td className="px-3 py-2 border-r border-slate-100 font-semibold">
                        <button
                          type="button"
                          onClick={() => setViewingQuotation(p.originalQuotation)}
                          className="text-blue-600 hover:text-blue-800 text-left font-semibold"
                        >
                          {p.quotationNo}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-slate-700 border-r border-slate-100 font-medium">
                        {p.customerId ? (
                          <button
                            type="button"
                            onClick={() => navigate(role === "accounts" ? `/accounts/customer/${p.customerId}` : `/customer/${p.customerId}`)}
                            className="text-blue-600 hover:text-blue-850 hover:underline font-semibold text-left"
                          >
                            {p.customerName}
                          </button>
                        ) : (
                          p.customerName
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-900 border-r border-slate-100 font-medium whitespace-pre-wrap">{p.productName}</td>
                      <td className="px-3 py-2 font-mono text-[10px] text-slate-600 border-r border-slate-100">{p.itemCode || "-"}</td>
                      <td className="px-3 py-2 text-slate-500 border-r border-slate-100">{p.hsnCode || "-"}</td>
                      <td className="px-3 py-2 text-right text-slate-900 font-semibold border-r border-slate-100">{p.quantity}</td>
                      <td className="px-3 py-2 text-slate-600 border-r border-slate-100">{p.unit || "No"}</td>
                      <td className="px-3 py-2 text-right text-slate-700 border-r border-slate-100">₹{(p.unitPrice || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2 text-right font-bold text-slate-950">₹{(p.totalPrice || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination View More for Products */}
            {filteredProducts.length > productsLimit && (
              <div className="mt-4 flex justify-center pb-2">
                <button
                  type="button"
                  onClick={() => setProductsLimit((prev) => prev + 20)}
                  className="rounded-[4px] bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-4 py-2 transition"
                >
                  View More
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {viewingQuotation && (
        <QuotationViewerModal
          quotation={viewingQuotation}
          onClose={() => setViewingQuotation(null)}
          onDuplicate={openDuplicateQuotationDraft}
          onPdf={() => openQuotationPdf(viewingQuotation)}
          quotations={quotations}
          onViewQuotation={setViewingQuotation}
        />
      )}
    </div>
  );
}

function QuotationViewerModal({ quotation, onClose, onDuplicate, onPdf, quotations = [], onViewQuotation }) {
  if (!quotation) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="relative w-full max-w-4xl rounded-[24px] border border-slate-200 bg-white p-6 shadow-2xl flex flex-col max-h-[90vh]"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-slate-100 pb-4 shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-slate-900">Quotation Details</h2>
              <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                quotation.status === "accepted" || quotation.status === "invoiced" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                quotation.status === "sent" ? "bg-blue-50 text-blue-700 border-blue-200" :
                quotation.status === "rejected" ? "bg-red-50 text-red-700 border-red-200" :
                quotation.status === "expired" ? "bg-amber-50 text-amber-700 border-amber-200" :
                "bg-slate-50 text-slate-650 border-slate-200"
              }`}>
                {quotation.status}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500 font-semibold">{quotation.quotationNo || "Draft Quotation"}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-slate-100 p-2 text-slate-500 transition hover:bg-slate-200 hover:text-slate-800 focus:outline-none"
            style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: "0" }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="mt-4 overflow-y-auto space-y-5 flex-1 pr-1">
          {/* Re-Quotation Revision Info Banner */}
          {quotation.sourceQuotationNo && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center justify-between text-xs animate-in fade-in duration-200">
              <div className="flex items-center gap-2 text-slate-600 font-semibold">
                <span className="material-symbols-outlined text-slate-400 text-sm">info</span>
                <span>This quotation is a revision of <strong>{quotation.sourceQuotationNo}</strong></span>
              </div>
              <button
                type="button"
                onClick={() => {
                  const parentQuote = quotations.find(
                    (item) => item._id === quotation.sourceQuotationId || item.quotationNo === quotation.sourceQuotationNo
                  );
                  if (parentQuote) {
                    onViewQuotation(parentQuote);
                  } else {
                    alert(`Original quotation ${quotation.sourceQuotationNo} could not be found.`);
                  }
                }}
                className="text-blue-650 hover:text-blue-800 font-bold flex items-center gap-1 transition cursor-pointer bg-transparent border-0 hover:underline"
              >
                View Original ➔
              </button>
            </div>
          )}

          {/* Metadata Cards */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-[16px] border border-slate-100 bg-slate-50/50 p-4 space-y-2">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Customer Details</h3>
              <div className="text-sm space-y-1">
                <p className="font-bold text-slate-900">{quotation.customerName || "-"}</p>
                {quotation.contactPerson && <p className="text-slate-600"><span className="font-semibold text-slate-500">Contact:</span> {quotation.contactPerson}</p>}
                {quotation.phone && <p className="text-slate-600"><span className="font-semibold text-slate-500">Phone:</span> {quotation.phone}</p>}
                {quotation.email && <p className="text-slate-600"><span className="font-semibold text-slate-500">Email:</span> {quotation.email}</p>}
                {quotation.gstNumber && <p className="text-slate-600"><span className="font-semibold text-slate-500">GST:</span> {quotation.gstNumber}</p>}
                {quotation.billingAddress && <p className="text-slate-600 mt-1 whitespace-pre-wrap"><span className="font-semibold text-slate-500">Billing Address:</span><br/>{quotation.billingAddress}</p>}
              </div>
            </div>

            <div className="rounded-[16px] border border-slate-100 bg-slate-50/50 p-4 space-y-2">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Quotation Information</h3>
              <div className="text-sm space-y-1">
                <p className="text-slate-600"><span className="font-semibold text-slate-500">Date:</span> {quotation.quotationDate ? new Date(quotation.quotationDate).toLocaleDateString("en-GB") : "-"}</p>
                <p className="text-slate-600"><span className="font-semibold text-slate-500">Valid Until:</span> {quotation.validUntil ? new Date(quotation.validUntil).toLocaleDateString("en-GB") : "-"}</p>
                {quotation.referenceNo && <p className="text-slate-600"><span className="font-semibold text-slate-500">Reference No:</span> {quotation.referenceNo}</p>}
                {quotation.subject && <p className="text-slate-600"><span className="font-semibold text-slate-500">Subject:</span> {quotation.subject}</p>}
                {quotation.deliveryTime && <p className="text-slate-600"><span className="font-semibold text-slate-500">Delivery Time:</span> {quotation.deliveryTime}</p>}
                {quotation.paymentTerms && <p className="text-slate-600"><span className="font-semibold text-slate-500">Payment Terms:</span> {quotation.paymentTerms}</p>}
                {quotation.warranty && <p className="text-slate-600"><span className="font-semibold text-slate-500">Warranty:</span> {quotation.warranty}</p>}
                {(quotation.gstType === "sez_supply" || quotation.lutRefNo) && (
                  <p className="text-slate-600"><span className="font-semibold text-slate-500">LUT:</span> <span className="font-bold text-slate-800">TAX EXEMPTED</span>{quotation.lutRefNo ? ` • ${quotation.lutRefNo}` : ""}</p>
                )}
              </div>
            </div>
          </div>

          {/* Products / Items Table */}
          <div className="space-y-2">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1">
              <span>🛒</span> Quoted Products
            </h3>
            <div className="overflow-x-auto rounded-[12px] border border-slate-200 bg-white">
              <table className="w-full min-w-[700px] text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold">
                    <th className="p-2.5 text-center border-r border-slate-100 w-10">#</th>
                    <th className="p-2.5 border-r border-slate-100 w-28">Item Code</th>
                    <th className="p-2.5 border-r border-slate-100 w-24">HSN Code</th>
                    <th className="p-2.5 border-r border-slate-100">Description</th>
                    <th className="p-2.5 text-right border-r border-slate-100 w-16">Qty</th>
                    <th className="p-2.5 border-r border-slate-100 w-16">Unit</th>
                    <th className="p-2.5 text-right border-r border-slate-100 w-24">List Price</th>
                    <th className="p-2.5 text-right border-r border-slate-100 w-24">Discount</th>
                    <th className="p-2.5 text-right border-r border-slate-100 w-24">Unit Price</th>
                    <th className="p-2.5 text-right w-24">Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(quotation.items || []).map((item, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50 align-middle">
                      <td className="p-2.5 text-center border-r border-slate-100 text-slate-400 font-semibold">{idx + 1}</td>
                      <td className="p-2.5 border-r border-slate-100 font-mono font-medium text-slate-600 uppercase">{item.itemCode || "-"}</td>
                      <td className="p-2.5 border-r border-slate-100 text-slate-500">{item.hsnCode || "-"}</td>
                      <td className="p-2.5 border-r border-slate-100 text-slate-800 font-medium whitespace-pre-wrap">{item.description}</td>
                      <td className="p-2.5 text-right border-r border-slate-100 text-slate-900 font-semibold">{item.quantity}</td>
                      <td className="p-2.5 border-r border-slate-100 text-slate-600">{item.unit || "NA"}</td>
                      <td className="p-2.5 text-right border-r border-slate-100 text-slate-600 font-medium">₹{(item.listPrice ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                      <td className="p-2.5 text-right border-r border-slate-100 text-red-600 font-medium">
                        {item.discountValue ? (item.discountType === "amount" ? `₹${item.discountValue.toLocaleString("en-IN")}` : `${item.discountValue}%`) : "-"}
                      </td>
                      <td className="p-2.5 text-right border-r border-slate-100 text-slate-700 font-semibold">₹{(item.unitPrice ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                      <td className="p-2.5 text-right text-slate-900 font-bold">₹{(item.totalAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                  {(!quotation.items || !quotation.items.length) && (
                    <tr>
                      <td colSpan="10" className="p-8 text-center text-slate-400">No products listed.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom Area: Terms & Summary */}
          <div className="grid gap-4 md:grid-cols-12">
            <div className="md:col-span-7 rounded-[16px] border border-slate-100 p-4 space-y-3">
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Terms & Remarks</h4>
              <div className="text-xs text-slate-600 space-y-2 leading-relaxed">
                {quotation.terms && <p><strong>Terms:</strong> {quotation.terms}</p>}
                {quotation.notes && <p><strong>Notes:</strong> {quotation.notes}</p>}
              </div>
            </div>

            <div className="md:col-span-5 rounded-[16px] border border-slate-200 bg-slate-50/50 p-4 space-y-2">
              <div className="flex justify-between text-xs font-bold text-slate-800">
                <span>Sub Total:</span>
                <span className="font-bold text-slate-900">₹{(quotation.subTotal ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
              {(quotation.overallDiscountPercent ?? 0) > 0 && (
                <>
                  <div className="flex justify-between text-xs text-red-600 font-semibold">
                    <span>Overall Discount ({quotation.overallDiscountPercent}%):</span>
                    <span>-₹{(quotation.overallDiscountAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-600 font-semibold">
                    <span>After Discount:</span>
                    <span>₹{((quotation.subTotal ?? 0) - (quotation.overallDiscountAmount ?? 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between text-xs text-slate-500 font-semibold">
                <span>GST:</span>
                <span>₹{(quotation.gstAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm font-bold text-slate-800 border-t border-slate-200 pt-2">
                <span>Grand Total:</span>
                <span className="text-blue-600">₹{(quotation.grandTotal ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="mt-6 flex items-center justify-end gap-2 border-t border-slate-100 pt-4 shrink-0">
          <button
            type="button"
            onClick={onPdf}
            className="rounded-[12px] bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 text-xs font-semibold flex items-center gap-1.5 transition"
          >
            <span>📄</span> View PDF
          </button>
          <button
            type="button"
            onClick={() => {
              onDuplicate(quotation);
              onClose();
            }}
            className="rounded-[12px] bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 text-xs font-semibold flex items-center gap-1.5 transition"
          >
            <span>👥</span> Duplicate to Other Customer
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-[12px] border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2 text-xs font-semibold transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div>
      {label && <label className="mb-1 block text-xs font-semibold text-slate-500">{label}</label>}
      <input {...props} className="w-full rounded-[4px] border border-slate-300 px-3 py-1.5 text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 h-9 bg-white" />
    </div>
  );
}

function Select({ label, options, ...props }) {
  return (
    <div>
      {label && <label className="mb-1 block text-xs font-semibold text-slate-500">{label}</label>}
      <select {...props} className="w-full rounded-[4px] border border-slate-300 bg-white px-2 py-1.5 text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 h-9">
        {options.map(([value, labelText]) => <option key={value} value={value}>{labelText}</option>)}
      </select>
    </div>
  );
}

function CheckboxInput({ label, ...props }) {
  return (
    <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer select-none py-1.5 px-3 bg-white rounded-[4px] border border-slate-200 h-9">
      <input {...props} type="checkbox" className="h-4 w-4 rounded-[4px] border-slate-300 text-blue-600 focus:ring-blue-500" />
      <span className="truncate">{label}</span>
    </label>
  );
}

function TableInput({ value, onChange, className = "", ...props }) {
  return (
    <input
      {...props}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={`w-full rounded-[4px] border border-slate-200 px-2 py-1 text-sm outline-none focus:border-blue-500 focus:bg-blue-50/10 bg-white h-8 ${className}`}
    />
  );
}

function HsnCodeInput({ value, onChange, index }) {
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const wrapperRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredSuggestions = HSN_CODE_SUGGESTIONS.filter(
    (s) => !value || s.code.includes(value) || s.label.toLowerCase().includes(value.toLowerCase())
  );

  return (
    <div ref={wrapperRef} className="relative w-full">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setShowSuggestions(true)}
        placeholder="HSN"
        className="w-full rounded-[4px] border border-slate-200 px-2 py-1 text-sm outline-none focus:border-blue-500 focus:bg-blue-50/10 bg-white h-8"
      />
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-0.5 rounded-[4px] border border-slate-200 bg-white shadow-lg overflow-hidden">
          {filteredSuggestions.map((suggestion) => (
            <button
              key={suggestion.code}
              type="button"
              onClick={() => {
                onChange(suggestion.code);
                setShowSuggestions(false);
              }}
              className={`w-full text-left px-2.5 py-1.5 text-xs hover:bg-blue-50 transition flex items-center gap-1.5 ${
                value === suggestion.code ? "bg-blue-50 text-blue-700 font-bold" : "text-slate-700"
              }`}
            >
              <span className="font-mono font-bold">{suggestion.code}</span>
              <span className="text-slate-400">–</span>
              <span className="text-slate-500">{suggestion.label.split(" – ")[1]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value, strong = false }) {
  return (
    <div className={`flex items-center justify-between rounded-[4px] bg-white px-3 py-2 border border-slate-200 ${strong ? "font-bold text-slate-900 text-base" : "text-sm text-slate-600"}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
